import React from 'react';
import { EnvelopeIcon, PhoneIcon, UserGroupIcon, ChartBarIcon, TrophyIcon, ShieldCheckIcon, SparklesIcon, BoltIcon, HandThumbUpIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const Management: React.FC = () => {
  const team = [
    {
      name: 'Mr. Mabirizi Peter',
      position: 'Co-Founder & Managing Director',
      description: 'Visionary leader with over 10 years of experience in the construction and plumbing industry, driving strategic growth and operational excellence across East Africa.',
      email: 'plencoplumbing@gmail.com',
      phone: '+256 751 503292',
      expertise: ['Strategic Planning', 'Business Development', 'Client Relations']
    },
    {
      name: 'Mr. Sekitooleko Andrew',
      position: 'Co-Founder & Technical Director',
      description: 'Expert in engineering and project management with extensive regional experience, ensuring technical excellence and quality delivery in all projects.',
      email: 'info@plencoplumbingltd.com',
      phone: '',
      expertise: ['Technical Engineering', 'Project Management', 'Quality Assurance']
    }
  ];

  const achievements = [
    {
      icon: TrophyIcon,
      title: 'Industry Excellence',
      description: 'Delivering efficient construction and plumbing services through our associate offices to clients outside Uganda',
      color: 'bg-blue-500'
    },
    {
      icon: UserGroupIcon,
      title: 'Expert Team',
      description: 'Evolved with a highly effective team of expertise, Networks for providing door to door services along with overseas',
      color: 'bg-green-500'
    },
    {
      icon: ChartBarIcon,
      title: 'Proven Track Record',
      description: 'Successfully handled transportation of conventional materials both locally and across the region',
      color: 'bg-orange-500'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Quality Commitment',
      description: 'Modern management approach ensuring we have the best trained and experienced personnel at every level',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="bg-white">
      <SEO 
        title="Management Team - Leadership | Plenco Plumbing & Construction Uganda"
        description="Meet the leadership team at Plenco Plumbing & Construction Co. Ltd. Our experienced management team leads Uganda's premier construction and plumbing company with expertise in project management, engineering, and construction services."
        keywords="Plenco management team, construction company leadership Uganda, plumbing company directors Kampala, experienced construction managers Uganda"
        canonicalUrl="http://www.plencoplumbingltd.com/management"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 px-4 py-1 rounded-full text-sm font-semibold mb-4">OUR LEADERSHIP</div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">Management</span> <span className="text-primary-200">Team</span>
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            Meet the experienced leadership team driving Plenco's success across East Africa
          </p>
        </div>
      </section>

      {/* Leadership Introduction */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/gallery/PLENCO-MM-1.png"
                alt="Plenco Management Team"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-4xl font-black text-gray-900 mb-6">
                <span className="text-primary">Leadership</span> <span className="text-gray-600">Excellence</span>
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Founded in 2013 by visionary entrepreneurs Mr. Mabirizi Peter and Mr. Sekitooleko Andrew, 
                  Plenco has grown from a concept to a leading multi-construction and plumbing company 
                  operating across East Africa.
                </p>
                <p>
                  Our leadership brings together decades of combined experience in engineering, construction, 
                  and business management. We employ a high standard of experienced personnel at each level 
                  and in every department.
                </p>
                <p>
                  Our modern management approach to human resources ensures that we have the best trained 
                  and experienced personnel, enabling us to deliver excellence on every project we undertake.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Our</span> <span className="text-gray-600">Achievements</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Notable milestones that demonstrate our commitment to excellence and innovation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-primary group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 ${achievement.color} w-14 h-14 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <achievement.icon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-primary transition-colors">
                      {achievement.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {achievement.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Core Values Section */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Our Core</span> <span className="text-gray-600">Values</span>
            </h2>
            <p className="text-gray-600 text-lg">Principles that guide our leadership and operations</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow text-center border-t-4 border-blue-500">
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-3">Integrity</h3>
              <p className="text-gray-600 text-sm">Honesty and transparency in all our business dealings and relationships</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow text-center border-t-4 border-green-500">
              <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                <SparklesIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-3">Excellence</h3>
              <p className="text-gray-600 text-sm">Uncompromising commitment to the highest quality standards</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow text-center border-t-4 border-orange-500">
              <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4">
                <HandThumbUpIcon className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-3">Accountability</h3>
              <p className="text-gray-600 text-sm">Taking full responsibility for our actions and deliverables</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow text-center border-t-4 border-purple-500">
              <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4">
                <BriefcaseIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-3">Professionalism</h3>
              <p className="text-gray-600 text-sm">Maintaining expert standards in every service we deliver</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-br from-primary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Company</span> <span className="text-gray-600">Highlights</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center border-l-4 border-primary">
              <div className="text-5xl font-black text-primary mb-2">2013</div>
              <div className="text-gray-600 font-semibold">Year Founded</div>
              <p className="text-gray-500 text-sm mt-2">Registered February 5th</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center border-l-4 border-blue-500">
              <div className="text-5xl font-black text-blue-600 mb-2">36+</div>
              <div className="text-gray-600 font-semibold">Team Members</div>
              <p className="text-gray-500 text-sm mt-2">Skilled Professionals</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center border-l-4 border-green-500">
              <div className="text-5xl font-black text-green-600 mb-2">4</div>
              <div className="text-gray-600 font-semibold">Countries</div>
              <p className="text-gray-500 text-sm mt-2">Across East Africa</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center border-l-4 border-orange-500">
              <div className="text-5xl font-black text-orange-600 mb-2">500+</div>
              <div className="text-gray-600 font-semibold">Projects</div>
              <p className="text-gray-500 text-sm mt-2">Successfully Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary to-primary-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black mb-4">Want to Work With Us?</h2>
          <p className="text-xl mb-8 text-primary-100">
            Get in touch to discuss your project requirements with our management team
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-black hover:bg-primary-50 transition-colors shadow-lg"
          >
            Contact Management
          </a>
        </div>
      </section>
    </div>
  );
};

export default Management;
